// What this module needs from the app, typed structurally: nothing here imports the app, so the
// module can move to a package. `src/app/register-raven.tsx` builds it from real values and hands it
// to `createRavenModule(host)` (module.ts). The HTTP clients are not members: they come from the
// shared registry, `getHttpClient` of `@vnetwork/margarita-core/http`.


import type { ComponentType } from 'react';
import type { HostBase } from '@vnetwork/margarita-shell/host-base';

export interface RavenHost extends Pick<HostBase, 'brand' | 'trackFeatureAction'> {
  /** Base URL of margarita-raven (`APP_RAVEN_API_URL`); unset keeps the @mgmt fan-out hooks. */
  ravenApiUrl: string | undefined;
  /** The `domain_add` analytics event of the setup wizard. */
  trackDomainAdd: (serviceType: string, domain?: string) => void;
  /** The Business Usage page, rendered inside the CDN console so its sidebar stays. */
  UsagePage: ComponentType<{ initialServiceId?: string }>;
}

let current: RavenHost | null = null;

/** Called once by `createRavenModule`, before any route renders. */
export function registerRavenHost(host: RavenHost): void {
  current = host;
}

/** The registered host. Throws when the module is used without `createRavenModule(host)`. */
function ravenHost(): RavenHost {
  if (!current) throw new Error('Raven: createRavenModule(host) was not called before the module rendered');
  return current;
}

/** `host.env`, resolved at call time so call sites read like the shared one. */
export const env: Pick<RavenHost, 'ravenApiUrl'> = {
  get ravenApiUrl() {
    return ravenHost().ravenApiUrl;
  },
};

/** `host.brand`, resolved at call time. */
export const brand: RavenHost['brand'] = {
  get shortName() {
    return ravenHost().brand.shortName;
  },
  emails: {
    get support() {
      return ravenHost().brand.emails.support;
    },
  },
};

/** `host.UsagePage`, resolved at render time. */
export const UsagePage: RavenHost['UsagePage'] = (props) => {
  const Page = ravenHost().UsagePage;
  return <Page {...props} />;
};

/** `host.trackFeatureAction`, resolved at call time. */
export const trackFeatureAction: RavenHost['trackFeatureAction'] = (service, feature, verb, params) =>
  ravenHost().trackFeatureAction(service, feature, verb, params);

/** `host.trackDomainAdd`, resolved at call time. */
export const trackDomainAdd: RavenHost['trackDomainAdd'] = (serviceType, domain) => ravenHost().trackDomainAdd(serviceType, domain);
